<script setup>
import { useForm } from '@inertiajs/vue3'
import { ref, computed, watch } from 'vue'
import TextInput from '@/Shared/Components/Forms/TextInput.vue'
import PrimaryButton from '@/Shared/Components/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Shared/Components/Buttons/SecondaryButton.vue'
import DatePicker from '@/Shared/Components/Forms/DatePicker.vue'
import JourneyIcon from '../../../../svg/journey-icon.svg'
import SwitchIcon from '../../../../svg/switch-icon.svg'
import LinkButton from '@/Shared/Components/Buttons/LinkButton.vue'
import CheckboxList from '@/App/Pages/Profile/Partials/CheckboxList.vue'
import SelectInput from '@/Shared/Components/Forms/SelectInput.vue'
import InputLabel from '@/Shared/Components/Forms/InputLabel.vue'
import Checkbox from '@/Shared/Components/Forms/Checkbox.vue'
import AutoComplete from './AutoComplete.vue'
import dayjs from 'dayjs'
import jpApi from '@/Shared/Services/jp.api.js'
import ErrorMessage from './ErrorMessage.vue'
import Cookies from 'js-cookie'
import { useJourneyStore } from '@/Public/Stores/JourneyStore'

const jpWebUrl = import.meta.env.VITE_JOURNEY_PLANNER_WEB_BASE_URL

let newJourneyStore = useJourneyStore()

const props = defineProps({})

const routeTypes = [
  { id: 'bus', title: 'Bus' },
  { id: 'train', title: 'Train' },
  { id: 'walk', title: 'Walk' },
  { id: 'bike', title: 'Cycle' },
  { id: 'drt', title: 'DRT' },
]

const sortOptions = [
  { id: 'travel-time', label: 'Travel time' },
  { id: 'emissions', label: 'Emissions' },
  { id: 'cost', label: 'Lowest Cost' },
  { id: 'depature-time', label: 'Departure time' },
  { id: 'arrival-time', label: 'Arrival time' },
  { id: 'wait-time', label: 'Wait time' },
  { id: 'walk-distance', label: 'Walking distance' },
  { id: 'least-changes', label: 'Least changes' },
]

const savedCookies = Cookies.get('journey-settings') != undefined ? JSON.parse(Cookies.get('journey-settings')) : false

const journeyType = ref('single')
const showAdvanced = ref(savedCookies ? true : false)
const formErrors = ref({})

const form = useForm({
  origin: {},
  destination: {},
  out_time: '',
  return_time: '',
  travel_types: savedCookies ? savedCookies.travel_types : ['bus', 'train', 'walk', 'bike', 'drt'],
  sort_by: savedCookies ? savedCookies.sort_by : 'travel-time',
  exclude_walk_type: savedCookies ? savedCookies.exclude_walk_type : 'time',
  exclude_walk_quantity: savedCookies ? savedCookies.exclude_walk_quantity : 45,
  remember: savedCookies ? savedCookies.remember : false,
})

const setJourneyType = (type) => {
  if (type === 'single') {
    form.reset('return_time')
  }
  journeyType.value = type
}
const swapDestination = () => {
  ;[form.origin, form.destination] = [form.destination, form.origin]
}

const updateTravelTypes = (travelType) => {
  form.travel_types = travelType
}

const minimumReturnDate = computed(() => {
  return form.out_time
})

const toggleAdvancedOptions = () => {
  showAdvanced.value = !showAdvanced.value
}

const hasError = (fieldName) => {
  return formErrors.value ? Object.keys(formErrors.value).includes(fieldName) : false
}

const showErrorMessage = () => {
  return Object.keys(formErrors.value).length
}

watch(
  () => newJourneyStore.formattedDestination,
  (newVal) => {
    form.destination = newVal
    document.getElementById('to').scrollIntoView({ behavior: 'smooth', block: 'center' })
  },
)

const submit = () => {
  if (form.remember) {
    setCookies()
  }
  //prevent sending data without travel type
  if (form.travel_types.length == 0) {
    showAdvanced.value = true
    return (formErrors.value = { 'Travel type': ['Travel type is required'] })
  }

  jpApi
    .search({
      origin: form.origin?.label,
      destination: form.destination?.label,
      out_time: form.out_time ? dayjs(form.out_time).format('YYYY/MM/DD HH:mm:ss') : null,
      return_time: form.return_time ? dayjs(form.return_time).format('YYYY/MM/DD HH:mm:ss') : null,
      has_car_access: 1,
      exclude_walk_type: form.exclude_walk_type,
      exclude_walk_quantity: form.exclude_walk_quantity,
    })
    .then((response) => {
      let url = new URL(jpWebUrl + `/search/${response.data.data.hash}/results`)
      url.searchParams.set('sort', form.sort_by)
      url.searchParams.set('filters', form.travel_types.join(','))
      window.location.href = url
    })
    .catch((error) => {
      formErrors.value = error.response.data.errors
    })
}

watch(form, (newForm) => {
  const inputData = Object.entries(newForm.data()).reduce((a, [k, v]) => (v ? ((a[k] = v), a) : a), {})
  removeErrors(Object.keys(inputData))
})

watch(
  () => [form.origin, form.destination],
  ([origin, destination]) => {
    if (origin && destination && !form.out_time) {
      form.out_time = new Date(new Date().getTime() + 5 * 60000)
    }
  },
)

watch(
  () => journeyType.value,
  (newValue) => {
    if (newValue === 'return' && !form.return_time && form.out_time) {
      form.return_time = new Date(new Date(form.out_time).getTime() + 2 * 60 * 60000)
    }
  },
)

const removeErrors = (formData) => {
  formData.forEach((value) => {
    if (formErrors.value[value]) {
      delete formErrors.value[value]
    }
  })
}

watch(
  () => form.remember,
  (newValue) => {
    if (newValue === false) {
      Cookies.remove('journey-settings')
    }
  },
)

const setCookies = () => {
  Cookies.set(
    'journey-settings',
    JSON.stringify({
      travel_types: form.travel_types,
      sort_by: form.sort_by,
      exclude_walk_type: form.exclude_walk_type,
      exclude_walk_quantity: form.exclude_walk_quantity,
      remember: form.remember,
    }),
    { expires: 7, path: '/' },
  )
}
</script>

<template>
  <div class="mx-auto p-4 bg-white shadow-lg rounded-lg">
    <form @submit.prevent="submit">
      <div class="flex justify-between mb-4">
        <div class="flex items-center">
          <JourneyIcon />
          <h2 class="font-semibold text-lg ml-3">Plan a journey</h2>
        </div>
        <ErrorMessage
          v-if="showErrorMessage"
          :errors="formErrors"
        />
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-1 mb-3">
        <div class="w-5/6 md:w-full md:flex items-center">
          <label
            for="from"
            class="text-sm font-semibold md:mr-5"
          >
            From
          </label>
          <AutoComplete
            id="from"
            v-model="form.origin"
            show-current-location
            placeholder="Enter your origin"
            class="w-full md:flex-1 py-2"
            :error="hasError('origin')"
          />
        </div>

        <div class="md:hidden relative">
          <LinkButton
            class="md:hidden absolute right-3"
            type="button"
            aria-label="swap destination"
            @click="swapDestination"
          >
            <SwitchIcon class="rotate-90" />
          </LinkButton>
        </div>
        <div class="w-5/6 md:w-full md:flex items-center">
          <div class="flex md:flex-row">
            <LinkButton
              type="button"
              aria-label="swap destination"
              @click="swapDestination"
            >
              <SwitchIcon
                class="hidden md:block md:mx-4 text-right"
                role="none"
              />
            </LinkButton>
            <label
              for="to"
              class="text-sm font-semibold md:mr-5"
            >
              To
            </label>
          </div>
          <AutoComplete
            id="to"
            v-model="form.destination"
            placeholder="Enter your destination"
            class="w-full md:flex-1 py-2"
            :error="hasError('destination')"
          />
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 mb-4">
        <div class="grid md:grid-cols-8 md:space-x-2 items-center">
          <SecondaryButton
            as="button"
            class="justify-center mr-4 md:mr-0 col-span-2"
            :is-active="journeyType === 'single'"
            @click="setJourneyType('single')"
          >
            Single
          </SecondaryButton>
          <SecondaryButton
            class="justify-center col-span-2"
            as="button"
            :is-active="journeyType === 'return'"
            @click="setJourneyType('return')"
          >
            Return
          </SecondaryButton>

          <div class="flex flex-col md:flex-row md:items-center col-span-4">
            <label
              for="outbound"
              class="text-sm font-semibold md:mb-0 md:w-auto md:mr-5 mt-5 md:mt-0"
            >
              Outbound
            </label>
            <DatePicker
              id="outbound"
              v-model="form.out_time"
              placeholder="Depart at"
              :min-date="new Date()"
              format="dd/MM/yyyy, HH:mm"
              class="w-full py-2"
            ></DatePicker>
          </div>
        </div>

        <div class="flex flex-col md:flex-row md:items-center md:justify-end">
          <div
            class="flex flex-col md:flex-row md:items-center"
            :class="journeyType !== 'return' ? 'hidden' : ''"
          >
            <label
              for="return"
              class="text-sm font-semibold md:ml-5 mb-1 md:mb-0 md:w-auto md:mr-8"
            >
              Return
            </label>
            <DatePicker
              id="return"
              v-model="form.return_time"
              placeholder="Depart at"
              :min-date="minimumReturnDate"
              format="dd/MM/yyyy, HH:mm"
              class="w-full py-2"
            />
          </div>

          <div class="flex justify-between items-center mt-4 md:hidden">
            <LinkButton @click.prevent="toggleAdvancedOptions">
              {{ showAdvanced ? 'Hide advanced options' : 'Show advanced options' }}
            </LinkButton>
          </div>

          <PrimaryButton class="w-full md:w-1/2 justify-center md:ml-4 mt-5 md:mt-0">Find routes & times</PrimaryButton>
        </div>
        <div class="mt-3 hidden md:block">
          <LinkButton @click.prevent="toggleAdvancedOptions">
            {{ showAdvanced ? 'Hide advanced options' : 'Show advanced options' }}
          </LinkButton>
        </div>
      </div>

      <div
        v-if="showAdvanced"
        class="mt-4"
      >
        <div class="border-t border-t-gray-400">
          <p class="mt-3 md:mb-3 font-bold">Show</p>
          <div>
            <div class="flex-col md:flex-row space-x-4 mb-5">
              <CheckboxList
                id="route_types"
                :option-list="routeTypes"
                :checked="form.travel_types"
                @checked-values-changed="updateTravelTypes"
              />
            </div>

            <!-- Order results and exclude walks -->
            <div class="grid grid-cols-1 md:grid-cols-4">
              <!-- Order by dropdown -->
              <div class="w-full mt-8 md:mt-0">
                <InputLabel
                  for="sort_by"
                  class="block font-bold"
                  value="Order result by"
                />
                <SelectInput
                  id="sort_by"
                  v-model="form.sort_by"
                  placeholder="Select sort"
                  value-key="id"
                  :items="sortOptions"
                >
                  <template #selected="{ selected }">
                    <span class="ml-3 block truncate">
                      {{ selected ? selected.label : '' }}
                    </span>
                  </template>
                  <template #default="{ item, selected }">
                    <span :class="[selected ? 'font-semibold' : 'font-normal', 'ml-3 block truncate']">
                      {{ item.label }}
                    </span>
                  </template>
                </SelectInput>
              </div>

              <!-- Exclude walks over -->
              <div class="md:ml-5 mt-8 md:mt-0">
                <InputLabel
                  for="exclude_walks"
                  class="block font-bold"
                  value="Exclude walks over"
                />
                <TextInput
                  id="exclude_walks"
                  v-model="form.exclude_walk_quantity"
                  type="number"
                  min="1"
                  placeholder="Enter"
                  class="w-full"
                />
              </div>

              <div class="flex md:mt-6 mt-4 md:ml-4">
                <label class="flex items-center">
                  <input
                    v-model="form.exclude_walk_type"
                    value="distance"
                    type="radio"
                    class="h-4 w-4 border-black"
                  />
                  <span class="ml-1">Miles</span>
                </label>
                <label class="flex items-center ml-4">
                  <input
                    v-model="form.exclude_walk_type"
                    value="time"
                    type="radio"
                    class="h-4 w-4 border-black"
                  />
                  <span class="ml-1">Minutes</span>
                </label>
              </div>

              <!-- Remember preferences -->
              <div class="flex md:justify-end mt-8 md:mt-6">
                <label class="flex items-center">
                  <Checkbox
                    v-model:checked="form.remember"
                    name="remeber"
                  />
                  <span class="ml-2">Remember my preferences</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>
